import type { Scenario } from '@tive/core';
import { clientId, type Viewer } from './api';
import { currentSession } from './auth';
import { useAppStore } from '../store/useAppStore';

type ServerEvent =
  | { type: 'hello'; headcount?: number; buildId?: string }
  | { type: 'pong' }
  | { type: 'scenario'; scenario: Scenario; origin: string; message?: string }
  | { type: 'scenario-list' }
  | { type: 'active-scenario'; scenarioId: string | null; origin?: string }
  | { type: 'org'; syncedAt: string; headcount: number }
  | { type: 'squads'; origin: string; message?: string }
  | { type: 'viewers'; scenarioId: string; viewers: Viewer[] };

/**
 * The label a socket sends alongside its token.
 *
 * A browser cannot put an Authorization header on a WebSocket — the API gives
 * you a URL and nothing else — so the token travels as the second subprotocol,
 * which is the one header the constructor does let you set. The first is this
 * label, because a subprotocol list of one that is a bearer token is an odd
 * thing to find in a proxy log.
 */
const WS_PROTOCOL = 'tive.auth.v1';

/** Closed by the server because the token was missing or would not verify. */
const UNAUTHORIZED = 4401;

const RECONNECT_MIN_MS = 500;
const RECONNECT_MAX_MS = 10_000;
/** Client-side keep-alive; the server also pings. */
const PING_MS = 20_000;

function socketUrl(): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/api/ws`;
}

/**
 * WebSocket connection to the API's change stream. Everything that mutates a
 * scenario — this tab, another tab, or Claude through the MCP server — is
 * broadcast here, so the chart stays current without polling or refreshing.
 *
 * Reconnects with exponential backoff and jitter; on every successful
 * reconnect it re-reads the scenario, because changes may have been missed
 * while the socket was down.
 */
export function connectLive(): () => void {
  const store = useAppStore.getState;

  let socket: WebSocket | null = null;
  let pingTimer: ReturnType<typeof setInterval> | undefined;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  let attempt = 0;
  let hadConnection = false;
  let closedByUs = false;

  const handle = (payload: ServerEvent): void => {
    switch (payload.type) {
      /*
       * A reconnect is when a tab finds out a deploy has happened: the socket
       * drops as the old revision goes and comes back against the new one.
       */
      case 'hello':
        store().noteBuild(payload.buildId);
        break;

      case 'pong':
        break;

      case 'scenario': {
        // Ignore the echo of our own optimistic update.
        if (payload.origin === clientId) break;

        // This tab is pinned to one scenario. A change to a different one is
        // worth knowing about — you may have it open next door — but it must
        // never move this tab.
        const open = store().scenario;
        if (open && payload.scenario.id !== open.id) {
          const who = payload.origin === 'claude' ? 'Claude' : 'Another tab';
          store().toast(
            'info',
            `${who} changed “${payload.scenario.name}”${payload.message ? `: ${payload.message}` : '.'}`,
          );
          void store().reloadScenarioList();
          break;
        }

        store().adoptRemoteScenario(payload.scenario, payload.message);
        break;
      }

      case 'scenario-list':
        void store().reloadScenarioList();
        break;

      /*
       * Only ever moves the server's default, which is what the *next* tab
       * opens with. Tabs already open keep the scenario they were opened on —
       * that is what lets two of them be compared side by side.
       */
      case 'active-scenario':
        void store().reloadScenarioList();
        break;

      case 'org':
        void store().bootstrap();
        break;

      /*
       * Teams are shared by every scenario, so this one is not filtered by
       * which scenario a tab has open — every tab wants it. The echo of our own
       * edit is skipped: the response to that request already updated us.
       */
      case 'squads': {
        if (payload.origin === clientId) break;
        void store().loadSquads();
        if (payload.message) {
          store().toast(payload.origin === 'claude' ? 'remote' : 'info', payload.message);
        }
        break;
      }

      /*
       * Who else has this plan open. Only ever about the scenario this tab is
       * showing — the server sends it to the people watching that one — but
       * checked anyway, because a stale frame arriving just after a switch
       * would otherwise put the wrong faces on screen.
       */
      case 'viewers': {
        if (payload.scenarioId !== store().scenario?.id) break;
        store().setViewers(payload.viewers);
        break;
      }

      default:
        break;
    }
  };

  const scheduleReconnect = (): void => {
    if (closedByUs) return;
    const backoff = Math.min(RECONNECT_MAX_MS, RECONNECT_MIN_MS * 2 ** attempt);
    const jittered = backoff * (0.7 + Math.random() * 0.6);
    attempt += 1;
    retryTimer = setTimeout(open, jittered);
  };

  /**
   * Tell the server which scenario this tab is showing, and say so again every
   * time the tab comes back to the front. That is how Claude knows which plan
   * "the org" means when the user does not name one — the one they are looking
   * at, rather than whichever happens to have been edited most recently.
   */
  const reportWatching = (): void => {
    const scenarioId = store().scenario?.id;
    if (!scenarioId || socket?.readyState !== WebSocket.OPEN) return;
    socket.send(JSON.stringify({ type: 'watching', scenarioId }));
  };

  function open(): void {
    if (closedByUs) return;

    /*
     * Signed in, the token goes in the subprotocol. Signed out — every local
     * run — there is nothing to send and the server asks for nothing.
     */
    const token = currentSession()?.idToken;
    socket = token ? new WebSocket(socketUrl(), [WS_PROTOCOL, token]) : new WebSocket(socketUrl());

    socket.addEventListener('open', () => {
      attempt = 0;
      store().setLive(true);
      reportWatching();

      pingTimer = setInterval(() => {
        if (socket?.readyState === WebSocket.OPEN) socket.send('ping');
      }, PING_MS);

      // Anything that happened while we were disconnected is picked up here.
      if (hadConnection) void store().bootstrap();
      hadConnection = true;
    });

    socket.addEventListener('message', (event: MessageEvent<string>) => {
      try {
        handle(JSON.parse(event.data) as ServerEvent);
      } catch {
        /* ignore malformed frames */
      }
    });

    socket.addEventListener('close', (event: CloseEvent) => {
      clearInterval(pingTimer);
      store().setLive(false);
      store().setViewers([]);
      /*
       * A rejected token is not something reconnecting fixes, and hammering
       * the server about it every half second is worse than stopping. The next
       * sign-in restarts this from scratch.
       */
      if (event.code === UNAUTHORIZED) {
        closedByUs = true;
        return;
      }
      scheduleReconnect();
    });

    // An error is always followed by a close, so reconnecting is handled there.
    socket.addEventListener('error', () => store().setLive(false));
  }

  open();

  // Bringing a tab to the front makes its scenario the one on screen, and
  // switching scenarios within a tab changes what it is showing. Both `focus`
  // and `visibilitychange` are listened for: switching between windows fires
  // the first, switching between tabs of one window the second.
  const onFocus = () => reportWatching();
  const onVisible = () => {
    if (document.visibilityState === 'visible') reportWatching();
  };
  window.addEventListener('focus', onFocus);
  document.addEventListener('visibilitychange', onVisible);
  const unsubscribeStore = useAppStore.subscribe((state, previous) => {
    if (state.scenario?.id !== previous.scenario?.id) reportWatching();
  });

  return () => {
    closedByUs = true;
    clearInterval(pingTimer);
    clearTimeout(retryTimer);
    window.removeEventListener('focus', onFocus);
    document.removeEventListener('visibilitychange', onVisible);
    unsubscribeStore();
    socket?.close();
    store().setLive(false);
  };
}
